import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ChevronRight, Phone, TrendingUp, TrendingDown, Minus, ArrowRight, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import InquiryForm from '../components/InquiryForm';
import SEOHead from '../components/SEOHead';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatCurrencyDetailed = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const SliderInput = ({ label, value, onChange, min, max, step, suffix, prefix, tooltip, testId }) => {
  const percentage = ((value - min) / (max - min)) * 100;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Label className="text-sm font-medium text-gray-700">{label}</Label>
          {tooltip && (
            <div className="group relative">
              <Info className="h-3.5 w-3.5 text-gray-400 cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none w-48 text-center z-50">
                {tooltip}
              </div>
            </div>
          )}
        </div>
        <div className="bg-gray-100 rounded-md px-3 py-1 text-sm font-semibold text-gray-900">
          {prefix}{typeof value === 'number' && value >= 1000 ? value.toLocaleString() : value}{suffix}
        </div>
      </div>
      <input
        data-testid={testId}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, #2563EB ${percentage}%, #e5e7eb ${percentage}%)`,
        }}
      />
      <div className="flex justify-between text-xs text-gray-400">
        <span>{prefix}{min.toLocaleString()}{suffix}</span>
        <span>{prefix}{max.toLocaleString()}{suffix}</span>
      </div>
    </div>
  );
};

const DSCRCalculatorPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Input state
  const [propertyValue, setPropertyValue] = useState(300000);
  const [downPaymentPct, setDownPaymentPct] = useState(25);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTerm, setLoanTerm] = useState(30);
  const [monthlyRent, setMonthlyRent] = useState(2500);
  const [propertyTax, setPropertyTax] = useState(300);
  const [insurance, setInsurance] = useState(150);
  const [hoaDues, setHoaDues] = useState(0);
  const [vacancyRate, setVacancyRate] = useState(5);
  const [managementFee, setManagementFee] = useState(8);
  const [maintenanceReserve, setMaintenanceReserve] = useState(5);

  const results = useMemo(() => {
    const loanAmount = propertyValue * (1 - downPaymentPct / 100);
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm * 12;

    // Monthly P&I
    let monthlyPI = 0;
    if (monthlyRate > 0) {
      monthlyPI = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
        (Math.pow(1 + monthlyRate, numPayments) - 1);
    } else {
      monthlyPI = loanAmount / numPayments;
    }

    // Monthly PITIA (what lenders use for DSCR)
    const monthlyPITIA = monthlyPI + propertyTax + insurance + hoaDues;

    // Gross monthly income
    const grossMonthlyIncome = monthlyRent;

    // DSCR (Lender's calculation: Gross Rent / PITIA)
    const dscr = monthlyPITIA > 0 ? grossMonthlyIncome / monthlyPITIA : 0;

    // Net Operating Income (for investor cash flow analysis)
    const vacancyLoss = grossMonthlyIncome * (vacancyRate / 100);
    const mgmtCost = grossMonthlyIncome * (managementFee / 100);
    const maintCost = grossMonthlyIncome * (maintenanceReserve / 100);
    const effectiveIncome = grossMonthlyIncome - vacancyLoss;
    const operatingExpenses = propertyTax + insurance + hoaDues + mgmtCost + maintCost;
    const monthlyNOI = effectiveIncome - operatingExpenses;
    const monthlyCashFlow = monthlyNOI - monthlyPI;
    const annualCashFlow = monthlyCashFlow * 12;
    const cashOnCash = (propertyValue * (downPaymentPct / 100)) > 0
      ? (annualCashFlow / (propertyValue * (downPaymentPct / 100))) * 100
      : 0;

    return {
      loanAmount,
      monthlyPI,
      monthlyPITIA,
      dscr,
      grossMonthlyIncome,
      effectiveIncome,
      operatingExpenses,
      monthlyNOI,
      monthlyCashFlow,
      annualCashFlow,
      cashOnCash,
      downPayment: propertyValue * (downPaymentPct / 100),
    };
  }, [propertyValue, downPaymentPct, interestRate, loanTerm, monthlyRent, propertyTax, insurance, hoaDues, vacancyRate, managementFee, maintenanceReserve]);

  const getDSCRStatus = (dscr) => {
    if (dscr >= 1.25) return { label: 'Strong', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', icon: TrendingUp, desc: 'Excellent qualification. Most lenders will approve.' };
    if (dscr >= 1.0) return { label: 'Qualifying', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', icon: TrendingUp, desc: 'Meets minimum DSCR requirements for most programs.' };
    if (dscr >= 0.75) return { label: 'Below Threshold', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', icon: Minus, desc: 'Below most lender minimums. Consider adjusting terms.' };
    return { label: 'Does Not Qualify', color: 'text-red-600', bg: 'bg-red-50 border-red-200', icon: TrendingDown, desc: 'DSCR too low. Increase rent or reduce loan amount.' };
  };

  const dscrStatus = getDSCRStatus(results.dscr);
  const StatusIcon = dscrStatus.icon;

  return (
    <div className="min-h-screen" data-testid="dscr-calculator-page">
      <SEOHead
        title="DSCR Calculator | Debt Service Coverage Ratio Tool | Close Now Lending"
        description="Free DSCR calculator for real estate investors. Calculate your Debt Service Coverage Ratio instantly to determine loan eligibility for rental properties. DSCR loans available nationwide."
        keywords="DSCR calculator, debt service coverage ratio, DSCR loan calculator, rental property calculator, investment property DSCR, real estate loan calculator"
        url="/dscr-calculator"
      />
      <Header />

      {/* Hero */}
      <section className="relative bg-slate-900 pt-28 pb-16">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-blue-400">DSCR Calculator</span>
          </nav>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <Calculator className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
                DSCR Calculator
              </h1>
            </div>
          </div>
          <p className="text-lg text-gray-300 max-w-2xl">
            Calculate your Debt Service Coverage Ratio to determine if your investment property qualifies for financing. Lenders typically require a minimum DSCR of 1.0.
          </p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 max-w-7xl mx-auto">

            {/* Inputs - Left Side */}
            <div className="lg:col-span-3 space-y-6">
              {/* Loan Details Card */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
                <h2 className="text-lg font-bold text-gray-900 mb-6">Loan Details</h2>
                <div className="space-y-6">
                  <SliderInput
                    label="Property Value"
                    value={propertyValue}
                    onChange={setPropertyValue}
                    min={50000} max={5000000} step={10000}
                    prefix="$" suffix=""
                    testId="input-property-value"
                  />
                  <SliderInput
                    label="Down Payment"
                    value={downPaymentPct}
                    onChange={setDownPaymentPct}
                    min={0} max={50} step={1}
                    prefix="" suffix="%"
                    tooltip="Percentage of purchase price paid upfront"
                    testId="input-down-payment"
                  />
                  <div className="text-sm text-gray-500 -mt-2 pl-1">
                    Loan Amount: <span className="font-semibold text-gray-800">{formatCurrency(results.loanAmount)}</span>
                    {' '} | Down Payment: <span className="font-semibold text-gray-800">{formatCurrency(results.downPayment)}</span>
                  </div>
                  <SliderInput
                    label="Interest Rate"
                    value={interestRate}
                    onChange={setInterestRate}
                    min={4} max={15} step={0.125}
                    prefix="" suffix="%"
                    testId="input-interest-rate"
                  />
                  <div className="flex items-center gap-4">
                    <Label className="text-sm font-medium text-gray-700 whitespace-nowrap">Loan Term</Label>
                    <div className="flex gap-2">
                      {[15, 20, 25, 30].map(term => (
                        <button
                          key={term}
                          data-testid={`term-${term}`}
                          onClick={() => setLoanTerm(term)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            loanTerm === term
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {term}yr
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Income Card */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
                <h2 className="text-lg font-bold text-gray-900 mb-6">Rental Income</h2>
                <div className="space-y-6">
                  <SliderInput
                    label="Monthly Rental Income"
                    value={monthlyRent}
                    onChange={setMonthlyRent}
                    min={500} max={20000} step={100}
                    prefix="$" suffix=""
                    tooltip="Expected gross monthly rental income"
                    testId="input-monthly-rent"
                  />
                  <SliderInput
                    label="Vacancy Rate"
                    value={vacancyRate}
                    onChange={setVacancyRate}
                    min={0} max={20} step={1}
                    prefix="" suffix="%"
                    tooltip="Expected percentage of time property is vacant"
                    testId="input-vacancy-rate"
                  />
                </div>
              </div>

              {/* Expenses Card */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
                <h2 className="text-lg font-bold text-gray-900 mb-6">Monthly Expenses</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">Property Tax (monthly)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <Input
                        data-testid="input-property-tax"
                        type="number"
                        value={propertyTax}
                        onChange={(e) => setPropertyTax(Number(e.target.value) || 0)}
                        className="pl-7"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">Insurance (monthly)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <Input
                        data-testid="input-insurance"
                        type="number"
                        value={insurance}
                        onChange={(e) => setInsurance(Number(e.target.value) || 0)}
                        className="pl-7"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">HOA / Association Dues</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <Input
                        data-testid="input-hoa"
                        type="number"
                        value={hoaDues}
                        onChange={(e) => setHoaDues(Number(e.target.value) || 0)}
                        className="pl-7"
                      />
                    </div>
                  </div>
                  <div>
                    <SliderInput
                      label="Management Fee"
                      value={managementFee}
                      onChange={setManagementFee}
                      min={0} max={15} step={1}
                      prefix="" suffix="%"
                      tooltip="Property management as % of gross rent"
                      testId="input-management-fee"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <SliderInput
                      label="Maintenance Reserve"
                      value={maintenanceReserve}
                      onChange={setMaintenanceReserve}
                      min={0} max={15} step={1}
                      prefix="" suffix="%"
                      tooltip="Maintenance/repairs reserve as % of gross rent"
                      testId="input-maintenance-reserve"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Results - Right Side (Sticky) */}
            <div className="lg:col-span-2">
              <div className="lg:sticky lg:top-24 space-y-6">
                {/* DSCR Result Card */}
                <div className={`rounded-2xl border-2 p-6 md:p-8 ${dscrStatus.bg}`}>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Your DSCR</p>
                    <div className="flex items-center justify-center gap-3 mb-3">
                      <span data-testid="dscr-result" className={`text-6xl font-bold ${dscrStatus.color}`}>
                        {results.dscr.toFixed(2)}
                      </span>
                      <StatusIcon className={`h-8 w-8 ${dscrStatus.color}`} />
                    </div>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${dscrStatus.color} bg-white/60`}>
                      {dscrStatus.label}
                    </span>
                    <p className="text-sm text-gray-600 mt-3">{dscrStatus.desc}</p>
                  </div>
                </div>

                {/* Monthly Breakdown */}
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Monthly Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Gross Rent</span>
                      <span className="text-sm font-semibold text-emerald-600">+{formatCurrencyDetailed(results.grossMonthlyIncome)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Mortgage (P&I)</span>
                      <span className="text-sm font-semibold text-red-600">-{formatCurrencyDetailed(results.monthlyPI)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Taxes & Insurance</span>
                      <span className="text-sm font-semibold text-red-600">-{formatCurrencyDetailed(propertyTax + insurance + hoaDues)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Operating Costs</span>
                      <span className="text-sm font-semibold text-red-600">-{formatCurrencyDetailed(results.operatingExpenses - propertyTax - insurance - hoaDues)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Vacancy Loss</span>
                      <span className="text-sm font-semibold text-red-600">-{formatCurrencyDetailed(monthlyRent * (vacancyRate / 100))}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3">
                      <span className="text-sm font-bold text-gray-900">Monthly Cash Flow</span>
                      <span className={`text-lg font-bold ${results.monthlyCashFlow >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {results.monthlyCashFlow >= 0 ? '+' : ''}{formatCurrencyDetailed(results.monthlyCashFlow)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Key Metrics</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-500 mb-1">Annual Cash Flow</p>
                      <p className={`text-base font-bold ${results.annualCashFlow >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(results.annualCashFlow)}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-500 mb-1">Cash-on-Cash Return</p>
                      <p className={`text-base font-bold ${results.cashOnCash >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {results.cashOnCash.toFixed(1)}%
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-500 mb-1">Monthly PITIA</p>
                      <p className="text-base font-bold text-gray-900">{formatCurrency(results.monthlyPITIA)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-500 mb-1">Monthly NOI</p>
                      <p className="text-base font-bold text-gray-900">{formatCurrency(results.monthlyNOI)}</p>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-slate-900 rounded-2xl p-6 text-center">
                  <h3 className="text-white font-bold mb-2">Ready to Finance?</h3>
                  <p className="text-gray-300 text-sm mb-4">Speak with a lending specialist about your deal.</p>
                  <Button
                    data-testid="calculator-apply-btn"
                    onClick={() => setIsFormOpen(true)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white mb-3"
                  >
                    Apply Now <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                  <a
                    href="tel:903-476-2937"
                    className="flex items-center justify-center gap-2 text-gray-300 hover:text-white text-sm transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    903-476-2937
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Educational Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Understanding DSCR</h2>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              The <strong>Debt Service Coverage Ratio (DSCR)</strong> is the most important metric lenders use to evaluate investment property loans. It measures the relationship between a property's income and its debt obligations, telling lenders whether the property generates enough revenue to cover its mortgage payments.
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-8">
              <p className="text-center text-lg font-mono font-bold text-gray-900">
                DSCR = Gross Monthly Rent / Monthly PITIA
              </p>
              <p className="text-center text-sm text-gray-500 mt-2">
                PITIA = Principal + Interest + Taxes + Insurance + Association Dues
              </p>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4">What DSCR Lenders Look For</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                <div className="text-2xl font-bold text-emerald-600 mb-1">1.25+</div>
                <div className="text-sm font-semibold text-emerald-700 mb-1">Strong</div>
                <div className="text-xs text-gray-600">Best rates and terms. Property cash flows well above debt service.</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <div className="text-2xl font-bold text-blue-600 mb-1">1.0 - 1.24</div>
                <div className="text-sm font-semibold text-blue-700 mb-1">Qualifying</div>
                <div className="text-xs text-gray-600">Meets minimum requirements. Property income covers debt payments.</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                <div className="text-2xl font-bold text-red-600 mb-1">Below 1.0</div>
                <div className="text-sm font-semibold text-red-700 mb-1">Does Not Qualify</div>
                <div className="text-xs text-gray-600">Property income doesn't cover debt. Consider adjusting deal terms.</div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4">Tips to Improve Your DSCR</h3>
            <ul className="space-y-3 mb-8">
              {[
                'Increase the down payment to reduce your monthly mortgage obligation',
                'Negotiate a lower purchase price to decrease the loan amount',
                'Look for properties with higher rental income potential',
                'Consider a longer loan term (30 years) to lower monthly payments',
                'Reduce expenses by shopping insurance rates or appealing property taxes',
                'Add value through renovations that justify higher rental rates',
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="mt-1.5 w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                  <span className="text-gray-700">{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-slate-900">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl font-bold text-white mb-4">Have a Deal in Mind?</h2>
          <p className="text-gray-300 mb-8 text-lg">
            Our lending team can help you analyze your investment property and find the right financing. Get in touch today for a personalized consultation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              data-testid="bottom-cta-apply-btn"
              onClick={() => setIsFormOpen(true)}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
            >
              Get Started <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <a href="tel:903-476-2937">
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-8"
              >
                <Phone className="h-5 w-5 mr-2" />
                Call 903-476-2937
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />

      <InquiryForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        loanType="DSCR Loan"
      />
    </div>
  );
};

export default DSCRCalculatorPage;
